import { useEffect, useState } from "react";

import {
  DraftChangeSummary,
  serverFetchDraftChangeSummary,
  serverPublishDraftPublication,
} from "api/api-server-publishing";
import { PillBanner } from "components/footer/PillBanner";
import { PillBannerButton } from "components/footer/PillBannerButton";
import { PublicationStatus } from "components/server-types";
import { t } from "i18n/i18n";
import { ConfirmationDialog } from "mds/components/ConfirmationDialog";
import { Dialog } from "mds/components/Dialog";
import { IconText } from "mds/components/IconText";
import { CircleCheckFilledIcon, CircleExclamationFilledIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  selectCurrentTermTitle,
  selectLatestPublicationForCurrentSection,
  selectLinkedSectionCountForCurrentSection,
} from "store/selectors";
import { toastErrorMessage } from "utils/alerts";

export const PublishBanner = () => {
  const linkedSectionCount = useAppSelector(selectLinkedSectionCountForCurrentSection);
  const termTitle = useAppSelector(selectCurrentTermTitle);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [changeSummary, setChangeSummary] = useState<DraftChangeSummary | null>(null);
  const latestPublication = useAppSelector(selectLatestPublicationForCurrentSection);
  const isDraft = !latestPublication || latestPublication.status === PublicationStatus.DRAFT;
  const isCompleted = latestPublication?.status === PublicationStatus.COMPLETED;
  const isPublishInProgress = latestPublication?.status === PublicationStatus.STARTED;
  const sectionId = latestPublication?.section_id;

  useEffect(() => {
    if (!isConfirmOpen || !sectionId) return;

    let cancelled = false;

    const fetchChangeSummary = async () => {
      try {
        const summary = await serverFetchDraftChangeSummary(sectionId);
        if (!cancelled) setChangeSummary(summary);
      } catch {
        // This is just summary data.  It doesn't block the publish button.
        toastErrorMessage(t("publishing.draft_change_summary_error"));
      }
    };

    fetchChangeSummary();

    return () => {
      cancelled = true;
    };
  }, [isConfirmOpen, sectionId]);

  const onPublish = async () => {
    if (!latestPublication || isPublishInProgress) return;

    try {
      await serverPublishDraftPublication(latestPublication.id);
    } catch {
      toastErrorMessage(t("publishing.publish_error"));
    }
  };

  if (linkedSectionCount === 0 || !latestPublication) {
    return null;
  }

  return (
    <>
      {(isDraft || isCompleted) && (
        <PillBanner
          buttonChildren={
            isDraft && (
              <PillBannerButton
                eventName="Publish template section"
                kind="banner-hibiscus"
                onClick={() => setIsConfirmOpen(true)}
              >
                {t("publishing.publish_changes")}
              </PillBannerButton>
            )
          }
          kind={isDraft ? "hibiscus" : "moss"}
          label="Publish banner"
          textChildren={
            isDraft ? (
              <IconText
                iconStart={<CircleExclamationFilledIcon className="text-hibiscus-tint-20" />}
                text={t("publishing.linked_sections_out_of_date")}
                truncateText
              />
            ) : (
              <IconText
                iconStart={<CircleCheckFilledIcon className="text-moss-tint-30" />}
                text={t("publishing.linked_sections_up_to_date")}
                truncateText
              />
            )
          }
        />
      )}

      <ConfirmationDialog
        confirmButtonText={t("publishing.publish_changes")}
        open={isConfirmOpen}
        title={t("publishing.publish_template_title")}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={onPublish}
      >
        <p>
          {t("publishing.publish_template_linked_sections", {
            count: linkedSectionCount,
            term: termTitle,
          })}
        </p>
        {changeSummary && (
          <p className="mt-4">
            {t("publishing.publish_template_details", {
              pageCount: changeSummary.pages_changed,
              topicCount: changeSummary.topics_changed,
            })}
          </p>
        )}
      </ConfirmationDialog>

      <Dialog
        dismissible={false}
        open={isPublishInProgress}
        title={t("publishing.publish_in_progress_title")}
        onClose={() => {}}
      >
        <p className="mt-2">
          {t("publishing.publish_in_progress_linked_sections", {
            count: linkedSectionCount,
            term: termTitle,
          })}
        </p>
        <p className="mt-4">{t("publishing.publish_in_progress_details")}</p>
      </Dialog>
    </>
  );
};
